import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router";
import { ChallengeScheme, LetterScheme } from "client-v2";
import { useDispatch, useSelector } from "react-redux";
import { actionsCreators, State } from "../../redux";
import { bindActionCreators } from "redux";

import Price from "../../components/section/donation/Price";
import Success from "../../components/section/success/Success";
import WizardContainer, {
	WizardContainerRef,
} from "../../components/container/WizardContainer";
import Letter from "../../components/section/letter/Letter";
import { Text } from "@adoptaunabuelo/react-components";
import EditLetter from "../../components/section/letter/EditLetter";
import PDFLetter from "../../components/section/letter/PDFLetter";
import PaymentMethod from "../../components/section/donation/PaymentMethod";
import SuccessCrossSell from "../../components/section/success/SuccessCrossSell";

const Checkout = (props: Props) => {
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch();
	const { setShowError } = bindActionCreators(actionsCreators, dispatch);
	const isMobile: boolean = useSelector(
		(state: State) => state.screen.isMobile
	);
	const currentUser = useSelector((state: State) => state.user.currentUser);
	const translation = useSelector(
		(state: State) => state.location.translation
	);

	const wizard = useRef<WizardContainerRef>(null);

	const view = "birthday";

	const [challenge, setChallenge] = useState<ChallengeScheme | undefined>(
		undefined
	);
	const [supporters, setSupporters] = useState(0);
	const [didSubscribe, setDidSubscribe] = useState<string | undefined>(
		undefined
	);
	const [letter, setLetter] = useState<{
		object?: LetterScheme;
		content: string;
		font: string;
		personalInfo: boolean;
		stamp: boolean;
		image?: string;
	}>({
		content: "",
		font: "Poppins",
		personalInfo: true,
		stamp: true,
	});
	const [selectedPrice, setSelectedPrice] = useState<number | undefined>(
		undefined
	);

	useEffect(() => {
		//init
		if (location.state && location.state.challenge) {
			setChallenge(location.state.challenge);
			setSupporters(location.state.supporters);
		} else {
			navigate("/");
		}
	}, []);

	return (
		<WizardContainer
			ref={wizard}
			views={[
				//Letter
				{
					centralContainerStyle: isMobile
						? {
								backgroundColor: "transparent",
								paddingTop: 8,
								maxHeight: "unset",
								borderRadius: 0,
						  }
						: {
								backgroundColor: "transparent",
								padding: 0,
								maxHeight: "unset",
						  },
					centralViewStyle: isMobile
						? {}
						: { width: "100%", maxWidth: 645 },
					centralView: currentUser && challenge && (
						<Letter
							user={currentUser}
							challenge={challenge}
							onFinish={(result) => {
								if (result.error) {
									setShowError({
										show: true,
										message: result.error,
									});
								} else if (result.data) {
									setLetter({
										...letter,
										content: result.data,
									});
									wizard.current?.goNext();
								}
							}}
						/>
					),
				},
				//Edit letter PDF
				{
					navigationLeftView: isMobile && (
						<Text type="b1" weight="medium">
							{translation.letter_edit_title_02}
						</Text>
					),
					leftViewStyle: {
						display: isMobile ? "none" : "flex",
					},
					leftView:
						currentUser && challenge ? (
							<EditLetter
								title="Haz tu carta única"
								subtitle="Personaliza tu carta antes de enviarla. Elige el estilo que más te guste y haz esta carta tan especial como tú."
								user={currentUser}
								challenge={challenge}
								view={view}
								{...letter}
								onChange={(data) =>
									setLetter({
										content: letter.content,
										...data,
									})
								}
								onFinish={(result) => {
									if (result.error) {
										setShowError({
											show: true,
											message: result.error,
										});
									} else if (result.data) {
										setLetter({
											...letter,
											object: result.data,
										});
										wizard.current?.goNext();
									}
								}}
							/>
						) : null,
					centralContainerStyle: {
						backgroundColor: "transparent",
					},
					centralViewStyle: { width: 550 },
					centralView:
						currentUser && challenge ? (
							<PDFLetter
								user={currentUser}
								challenge={challenge}
								view={view}
								{...letter}
								onChange={(data) =>
									setLetter({
										content: letter.content,
										...data,
									})
								}
								onFinish={(result) => {
									if (result.error) {
										setShowError({
											show: true,
											message: result.error,
										});
									} else if (result.data) {
										setLetter({
											...letter,
											object: result.data,
										});
										wizard.current?.goNext();
									}
								}}
							/>
						) : undefined,
				},
				//Price
				{
					centralView: challenge && (
						<Price
							view={view}
							title="Colabora para hacer su cumple más especial"
							subtitle="Todo lo recaudado irá destinado a comprar los regalos."
							subtitle_type=""
							type_to_eur={1}
							challenge={challenge}
							onSkip={() => wizard.current?.goNext(2)}
							onFinish={(result) => {
								if (result.error) {
									setShowError({
										show: true,
										message: result.error,
									});
								} else if (result.data) {
									setSelectedPrice(result.data.price);
									wizard.current?.goNext();
								}
							}}
						/>
					),
				},
				//Select payment method
				{
					centralView:
						selectedPrice && currentUser && challenge ? (
							<PaymentMethod
								challenge={challenge}
								amount={selectedPrice}
								user={currentUser}
								view={view}
								onFinish={(result) => {
									if (result.error) {
										setShowError({
											show: true,
											message: result.error,
										});
									} else if (result.data) {
										setDidSubscribe("unique");
										wizard.current?.goNext();
									}
								}}
							/>
						) : null,
				},
				//Success view
				{
					containerStyle: { overflowY: "scroll" },
					centralView:
						currentUser && challenge ? (
							<>
								<Success
									title={`¡Muchas gracias, ${currentUser.name}!`}
									subtitle="Hemos recibido tu felicitación. Gracias a gente como tú hacemos un poquito más felices a nuestros mayores."
									type="challenge"
									challenge={challenge}
									user={currentUser}
									letter={letter.object}
									didSubscribe={didSubscribe}
									steps={[
										{
											title: "Juntamos vuestras felicitaciones",
											subtitle:
												"Las recopilamos y se las hacemos llegar a la persona mayor. ¡Le van a encantar!",
										},
										{
											title: "Compramos los regalos",
											subtitle:
												"Gracias a vuestra colaboración haremos este día un poquito más especial.",
										},
										{
											title: "¡Hora de celebrar!",
											subtitle:
												"Organizaremos una fiesta para celebrar su cumpleaños y hacerle llegar todas vuestras felicitaciones y regalos. ¡Os lo contaremos todo por nuestras redes!",
										},
									]}
								/>
							</>
						) : null,
					rightView: !isMobile ? <SuccessCrossSell /> : null,
					hideBackButton: true,
				},
			]}
		/>
	);
};
export default Checkout;
export interface Props {}
